import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Chip, CircularProgress, Alert, Tabs, Tab, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Stack, IconButton, Tooltip, useTheme, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  DeleteOutline as DeactivateIcon,
  Edit as RenameIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getWarehouseStock, warehouseLabel, type WarehouseStock,
} from '../../services/warehouseService';
import warehouseLocationService from '../../services/warehouseLocationService';

/**
 * WAREHOUSE-DETAIL.1 — one warehouse: its locations, its stock, its movements.
 *
 * This is where LOCATIONS live now. They used to sit at
 * /admin/venues/:venueId/locations, which meant reaching a shelf by first
 * picking a venue — and for a shared cellar that venue was arbitrary, since any
 * venue with a grant reached the same shelves. It is also what made the
 * venue-scoped create land in the wrong store: it never took a warehouse, so
 * "General Stock" added to Alfred's went to Main Store.
 *
 * A location belongs to a warehouse. This is the warehouse.
 *
 * WHAT THE PERMISSIONS ARE FOR. The server resolves them and sends them with
 * the page, so the controls a reader cannot use are visibly disabled rather
 * than failing on click. A venue granted SELL on a shared cellar sees its stock
 * and cannot restructure it — the distinction MANAGE_LOCATIONS draws.
 */

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

const WarehouseDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { warehouseId } = useParams<{ warehouseId: string }>();

  const [data, setData] = useState<WarehouseStock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    if (!warehouseId) return;
    try {
      setData(await getWarehouseStock(warehouseId));
      setError('');
    } catch {
      setError('Could not load this warehouse.');
    } finally {
      setLoading(false);
    }
  }, [warehouseId]);

  useEffect(() => { load(); }, [load]);

  const mayManage = Boolean(data?.permissions.includes('MANAGE_LOCATIONS'));

  // Every write reports the server's own message. "That name is taken in this
  // warehouse" and "it has open draft transfers" are both actionable; "failed"
  // sends the operator round the same loop.
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e?.response?.data?.error?.message ?? 'That did not work. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const addLocation = () => {
    const name = newName.trim();
    if (!name || !warehouseId) return;
    run(async () => {
      await warehouseLocationService.create(warehouseId, { name });
      setNewName('');
      setAddOpen(false);
    });
  };

  const rename = (id: string, current: string) => {
    const name = window.prompt('Rename location', current)?.trim();
    if (!name || name === current || !warehouseId) return;
    run(() => warehouseLocationService.update(warehouseId, id, { name }));
  };

  const deactivate = (id: string, name: string) => {
    if (!warehouseId) return;
    if (!window.confirm(`Deactivate "${name}"? Stock already counted there stays on the books.`)) return;
    run(() => warehouseLocationService.deactivate(warehouseId, id));
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress /></Box>;
  }
  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Warehouse not found.'}</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/stock')} sx={{ mt: 2, textTransform: 'none' }}>
          Back to warehouses
        </Button>
      </Box>
    );
  }

  const { warehouse: w, totals } = data;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/stock')}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary, mb: 1, ml: -1 }}
        >
          Warehouses
        </Button>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
          {/* WAREHOUSE-LABEL.1 — same rule as the list it was opened from. Two
              screens showing the same thing must not name it differently, and
              "Main Store" alone identifies nothing inside a company. */}
          <Typography variant="h5" fontWeight={700}>{warehouseLabel(w)}</Typography>
          {w.isShared
            ? <Chip size="small" label="Shared" variant="outlined" />
            : <Chip size="small" label="Venue store" color="primary" variant="outlined" />}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {totals.locations} location{totals.locations === 1 ? '' : 's'}
          {' · '}{totals.items} item{totals.items === 1 ? '' : 's'} in stock
          {' · '}{fmt(totals.units)} units
        </Typography>
      </Box>

      {error && <Alert severity="warning" onClose={() => setError('')} sx={{ borderRadius: 1 }}>{error}</Alert>}

      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Tabs value={tab} onChange={(_, t) => setTab(t)} sx={{ borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, px: 1 }}>
          {/* Stock leads. What is in here is the daily question; the shape of
              the warehouse is set up once and then rarely touched, so opening
              on Locations put the seldom-changed thing in front of the reason
              anyone came. */}
          <Tab label={`Stock (${data.items.length})`} sx={{ textTransform: 'none' }} />
          <Tab label={`Locations (${data.locations.length})`} sx={{ textTransform: 'none' }} />
          <Tab label="Movements" sx={{ textTransform: 'none' }} />
        </Tabs>

        {tab === 1 && (
          <Box>
            {/* Adding a location is occasional and deliberate, so it is a
                dialog rather than a form strip sitting above the table. An
                inline field that is nearly always ignored is clutter on the
                view people actually came for, and it reads as though the page
                is asking for something. */}
            {mayManage && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                <Button
                  variant="contained" startIcon={<AddIcon />}
                  onClick={() => { setNewName(''); setAddOpen(true); }}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Add location
                </Button>
              </Box>
            )}
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Units</TableCell>
                    <TableCell align="right" sx={{ width: 110 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.locations.length === 0 && (
                    <TableRow><TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No locations yet. {mayManage
                          ? 'Add one above — stock has to live somewhere before it can be received.'
                          : 'Someone who can manage this warehouse needs to add one.'}
                      </Typography>
                    </TableCell></TableRow>
                  )}
                  {data.locations.map(l => (
                    <TableRow key={l._id} hover>
                      <TableCell>{l.name}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(l.units)}</TableCell>
                      <TableCell align="right">
                        {mayManage && (
                          <>
                            <Tooltip title="Rename">
                              <IconButton size="small" disabled={busy} onClick={() => rename(l._id, l.name)}>
                                <RenameIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Deactivate">
                              <IconButton size="small" disabled={busy} onClick={() => deactivate(l._id, l.name)}>
                                <DeactivateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tab === 0 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Where</TableCell>
                  <TableCell align="right">Units</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.length === 0 && (
                  <TableRow><TableCell colSpan={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nothing in stock here.
                    </Typography>
                  </TableCell></TableRow>
                )}
                {data.items.map(i => (
                  <TableRow key={i.inventoryItemId} hover>
                    <TableCell>{i.displayName ?? i.inventoryItemId}</TableCell>
                    <TableCell>
                      {/* The breakdown is the point: "12 on hand" is useless to
                          someone standing in front of one of two shelves. */}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {i.byLocation.filter(b => b.units !== 0).map(b => (
                          <Chip key={b.locationId} size="small" variant="outlined"
                            label={`${b.locationName ?? '—'}: ${fmt(b.units)}`} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                      {fmt(i.units)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {tab === 2 && (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.movements.length === 0 && (
                  <TableRow><TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      Nothing has moved here yet.
                    </Typography>
                  </TableCell></TableRow>
                )}
                {data.movements.map(m => (
                  <TableRow key={m._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(m.postedAt).toLocaleString()}
                    </TableCell>
                    <TableCell>{m.type.replace(/_/g, ' ')}</TableCell>
                    <TableCell>{m.displayName ?? m.inventoryItemId}</TableCell>
                    <TableCell>{m.locationName ?? '—'}</TableCell>
                    <TableCell align="right" sx={{
                      fontVariantNumeric: 'tabular-nums',
                      color: m.quantity < 0 ? theme.palette.error.main : theme.palette.success.main,
                    }}>
                      {m.quantity > 0 ? '+' : ''}{fmt(m.quantity)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Add location</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A place inside {w.name} that stock can sit in — a shelf, a rack, a
            room. Names only need to be unique within this warehouse.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && newName.trim()) addLocation(); }}
            placeholder="e.g. Bin 14"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={addLocation}
            disabled={busy || !newName.trim()} sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarehouseDetail;
